import React, { useState, useEffect } from 'react';
import { FaSearch, FaPlus, FaPen, FaTrash } from 'react-icons/fa';

// Order status options
const orderStatuses = ['Delivered', 'Completed', 'In Cart', 'Pending'];

const OrderRow = ({ order, onClickRow, onStatusChange, onDelete }) => (
  <tr
    key={order.id}
    onClick={() => onClickRow(order)}
    className="border-b hover:bg-gray-100 cursor-pointer"
  >
    <td className="py-4 px-6 text-blue-600 font-semibold">#{order.id}</td>
    <td className="py-4 px-6">{order.purchaseDate}</td>
    <td className="py-4 px-6">
      <img src={order.productImage} alt="Product" className="w-8 h-8" />
    </td>
    <td className="py-4 px-6">{order.customerName}</td>
    <td className="py-4 px-6">{order.email}</td>
    <td className="py-4 px-6">{order.status}</td>
    <td className="py-4 px-6">{order.paymentType}</td>
    <td className="py-4 px-6">
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent row click
          onStatusChange(order);
        }}
        className="text-blue-600 hover:text-blue-800 font-semibold"
      >
        Change Status
      </button>
    </td>
    <td className="py-4 px-6">
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent row click
          onDelete(order);
        }}
        className="text-red-600 hover:text-red-800 font-semibold"
      >
        <FaTrash />
      </button>
    </td>
  </tr>
);

const OrderModal = ({ order, onClose, onSave, editableOrder, isEditMode, onChange }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
    <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg relative">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-red-600">
        <FaPen className="w-6 h-6" />
      </button>

      <h3 className="text-2xl font-bold mb-6">{isEditMode ? 'Edit Order' : 'Order Details'}</h3>

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <strong>Order ID:</strong>
            <input
              type="text"
              value={editableOrder.id}
              onChange={(e) => onChange('id', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300"
              disabled={!isEditMode}
            />
          </div>
          <div>
            <strong>Customer Name:</strong>
            <input
              type="text"
              value={editableOrder.customerName}
              onChange={(e) => onChange('customerName', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300"
              disabled={!isEditMode}
            />
          </div>
          <div>
            <strong>Status:</strong>
            <select
              value={editableOrder.status}
              onChange={(e) => onChange('status', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300"
              disabled={!isEditMode}
            >
              {orderStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div>
            <strong>Email:</strong>
            <input
              type="email"
              value={editableOrder.email}
              onChange={(e) => onChange('email', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300"
              disabled={!isEditMode}
            />
          </div>
          <div>
            <strong>Payment Type:</strong>
            <input
              type="text"
              value={editableOrder.paymentType}
              onChange={(e) => onChange('paymentType', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300"
              disabled={!isEditMode}
            />
          </div>
          <div>
            <strong>Address:</strong>
            <textarea
              value={editableOrder.address}
              onChange={(e) => onChange('address', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300"
              disabled={!isEditMode}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mt-8">
          {isEditMode && (
            <button
              onClick={onSave}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-700 hover:text-red-600 px-6 py-3 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
);

const OrderManagement = () => {
  
  const [orders, setOrders] = useState([
    {
      id: 'RB5625',
      purchaseDate: '2024-04-29',
      productImage: 'https://via.placeholder.com/50',
      customerName: 'Anna M. Hines',
      email: 'anna.hines@mail.com',
      status: 'Pending',
      paymentType: 'Credit Card',
      address: 'Burr Ridge/Illinois',
    },
    {
      id: 'RB5626',
      purchaseDate: '2024-04-30',
      productImage: 'https://via.placeholder.com/50',
      customerName: 'John Doe',
      email: 'john.doe@mail.com',
      status: 'In Cart',
      paymentType: 'PayPal',
      address: 'Chicago/Illinois',
    },
    // More sample orders
  ]);

  const [editableOrder, setEditableOrder] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleRowClick = (order) => {
    setSelectedProduct(order);
    setEditableOrder({ ...order });
  };

  const handleEditClick = (order) => {
    setEditableOrder({ ...order });
    setIsEditMode(true);
  };

  const handleStatusChange = (order) => {
    const updatedOrder = { ...order, status: 'Completed' }; // Example: Update status to Completed
    setOrders((prevOrders) =>
      prevOrders.map((o) => (o.id === order.id ? updatedOrder : o))
    );
  };

  const handleDelete = (order) => {
    setOrders((prevOrders) => prevOrders.filter((o) => o.id !== order.id));
  };

  const handleSaveChanges = () => {
    if (!editableOrder.id || !editableOrder.customerName) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === editableOrder.id ? editableOrder : order
      )
    );
    setIsEditMode(false);
    setSelectedProduct(null);
    setEditableOrder(null);
    setErrorMessage('');
  };

  const handleCloseModal = () => {
    setIsEditMode(false);
    setEditableOrder(null);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleChange = (field, value) => {
    setEditableOrder((prev) => ({ ...prev, [field]: value }));
  };

  const filteredOrders = orders.filter(
    (order) =>
      order.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto p-8">
      <h1 className="text-3xl font-bold mb-6">Order Management</h1>

      {errorMessage && (
        <div className="bg-red-600 text-white p-4 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      <div className="mb-6 flex items-center justify-between">
        <input
          type="text"
          placeholder="Search by Customer or Order ID"
          value={searchQuery}
          onChange={handleSearchChange}
          className="px-4 py-2 border border-gray-300 rounded-lg w-1/3"
        />
        <button
          className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700"
          onClick={() => setIsEditMode(true)}
        >
          <FaPlus className="w-5 h-5" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse">
          <thead>
            <tr>
              <th className="py-4 px-6">Order ID</th>
              <th className="py-4 px-6">Purchase Date</th>
              <th className="py-4 px-6">Product</th>
              <th className="py-4 px-6">Customer</th>
              <th className="py-4 px-6">Email</th>
              <th className="py-4 px-6">Status</th>
              <th className="py-4 px-6">Payment</th>
              <th className="py-4 px-6">Actions</th>
              <th className="py-4 px-6">Delete</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                onClickRow={handleRowClick}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </tbody>
        </table>
      </div>

      {selectedProduct && (
        <OrderModal
          order={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveChanges}
          editableOrder={editableOrder}
          isEditMode={isEditMode}
          onChange={handleChange}
        />
      )}
    </div>
  );
};

export default OrderManagement;
